import React, { useState } from 'react';
import useChatStore from '../../stores/useChatStore';
import FlerpieInvitePicker from './FlerpieInvitePicker';
import useUserStore from '../../stores/useUserStore';
import useIsMobile from './useIsMobile';

const FlerpieChatHeader: React.FC = () => {
  const isMobile = useIsMobile();
  const toggleOpen = useChatStore((s: any) => s.toggleOpen);
  const toggleSidebar = useChatStore((s: any) => s.toggleSidebar);
  const isSidebarOpen = useChatStore((s: any) => s.isSidebarOpen);
  const createConversation = useChatStore((s: any) => s.createConversation);
  const isStreaming = useChatStore((s: any) => s.isStreaming);
  const activeConversationId = useChatStore((s: any) => s.activeConversationId);
  const activeThread = useChatStore((s: any) => s.activeThread);
  const pendingNotes = useChatStore((s: any) => s.pendingNotes);
  const onNotes = activeThread?.kind === 'notes';
  const [inviting, setInviting] = useState(false);
  const myId = useUserStore((s: any) => s.user.id);
  const withNames = activeThread && activeThread.shared ? activeThread.members.filter((m) => m.id !== myId).map((m) => m.label.split(' ')[0]) : [];

  return (
    <div
      // z-20: the header owns the invite picker, which must paint above the thread below it.
      className="relative z-20 flex items-center gap-2 px-3 py-2 flex-shrink-0"
      style={{
        borderBottom: '1px solid rgba(255,204,51,0.06)',
        background: 'rgba(30,30,30,0.8)',
        backdropFilter: 'blur(12px)',
        // Docked on desktop, the panel's corners are square.
        borderRadius: isMobile ? '16px 16px 0 0' : 0,
      }}
    >
      {/* Sidebar toggle */}
      <button
        onClick={toggleSidebar}
        aria-label={pendingNotes > 0
          ? `Conversations, ${pendingNotes} ${pendingNotes === 1 ? 'note' : 'notes'} waiting`
          : 'Conversations'}
        className={`relative w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200
                   ${isSidebarOpen
                     ? 'bg-primary/10 text-primary'
                     : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/60'}`}
        title="Conversations"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <rect x="1" y="2" width="14" height="2" rx="1" fill="currentColor"/>
          <rect x="1" y="7" width="10" height="2" rx="1" fill="currentColor"/>
          <rect x="1" y="12" width="14" height="2" rx="1" fill="currentColor"/>
        </svg>
        {pendingNotes > 0 && (
          <span
            aria-hidden="true"
            className="absolute top-0 right-0 min-w-[14px] h-[14px] px-1 rounded-full bg-primary text-[9px] font-semibold leading-none tabular-nums text-black flex items-center justify-center"
          >
            {pendingNotes}
          </span>
        )}
      </button>

      {/* Title */}
      <div className="flex-1 min-w-0">
        <h3
          className="text-sm font-semibold leading-tight"
          style={{ color: '#ffcc33' }}
        >
          Flerpie
        </h3>
        <p className="text-[10px] text-gray-500 leading-tight truncate">
          {onNotes
            ? 'Notes from your coworkers'
            : withNames.length ? `Thread with ${withNames.join(', ')}` : 'Firefly Analytics Assistant'}
        </p>
      </div>

      {/* Bring someone in (the notes thread is yours alone) */}
      {activeConversationId && !onNotes && activeThread && activeThread.mine !== false && (
        <button
          onClick={() => setInviting((v) => !v)}
          disabled={isStreaming}
          aria-label="Bring someone into this thread"
          aria-expanded={inviting}
          className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200 disabled:opacity-40
                     ${inviting ? 'bg-primary/10 text-primary' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/60'}`}
        >
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="6" cy="5" r="2.6" stroke="currentColor" strokeWidth="1.6"/>
            <path d="M1.5 13.5C1.5 10.8 3.5 9.2 6 9.2C7 9.2 7.9 9.45 8.6 9.9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            <path d="M12.5 8.5V14M9.75 11.25H15.25" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
        </button>
      )}
      {inviting && activeConversationId && !onNotes && activeThread && activeThread.mine !== false && (
        <FlerpieInvitePicker conversationId={activeConversationId} onClose={() => setInviting(false)} />
      )}

      {/* New chat */}
      <button
        onClick={() => createConversation()}
        disabled={isStreaming}
        aria-label="New chat"
        className="w-8 h-8 flex items-center justify-center rounded-lg
                   text-gray-500 hover:text-gray-300 hover:bg-gray-800/60
                   transition-all duration-200 disabled:opacity-40"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </button>

      {/* Close button */}
      <button
        onClick={toggleOpen}
        className="w-8 h-8 flex items-center justify-center rounded-lg
                   text-gray-500 hover:text-gray-300 hover:bg-gray-800/60
                   transition-all duration-200"
        title="Close"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </button>
    </div>
  );
};

export default FlerpieChatHeader;
